import React, { useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { Eye, EyeOff, Check, Info } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../components/ui/form";
import { api } from "../api/client";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "../hooks/useTranslation";
import { describeApiError } from "../utils/apiErrors";
import { CLERK_ENABLED } from "../lib/clerk";

const PRICING_TERMS_URL = "https://pingup.ai/pricing";

const SignInPrompt = ({ t }) => (
  <p className="text-base leading-7 text-muted-foreground text-center">
    {t("auth.alreadyHaveAccount")}{" "}
    <Link
      to="/login"
      className="font-semibold text-[#00E88D] underline underline-offset-2 hover:text-[#00c878]"
    >
      {t("auth.signInHere")}
    </Link>
  </p>
);

const Register = () => {
  // Clerk flag-gated: niente pagina di registrazione dedicata — si torna
  // alla landing col prompt e lì si apre la modale Clerk in overlay (stessa
  // esperienza del "Crea il mio assistente", stile Lovable).
  if (CLERK_ENABLED) {
    return <Navigate to="/" state={{ clerkModal: "signUp" }} replace />;
  }
  return <NativeRegister />;
};

const NativeRegister = () => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refresh } = useAuth();

  const form = useForm({
    defaultValues: {
      firstName: "",
      lastName: "",
      email: searchParams.get("email") || "",
      password: "",
      confirmPassword: "",
      consent: false,
    },
  });

  const password = form.watch("password");
  const confirmPassword = form.watch("confirmPassword");
  const passwordsMatch =
    confirmPassword.length > 0 && confirmPassword === password;

  const onSubmit = async (data) => {
    setIsLoading(true);
    setError("");

    try {
      const registrationData = {
        name: `${data.firstName} ${data.lastName}`.trim(),
        email: data.email,
        password: data.password,
      };

      await api.post("/auth/signup", registrationData);

      // Refresh auth state to get user data
      const refreshSuccess = await refresh();

      if (!refreshSuccess) {
        throw new Error(
          "Failed to authenticate after registration. Please try logging in."
        );
      }

      // Arrivato dal link di un invito: si torna li', e la pagina dell'invito
      // lo accetta da sola (la registrazione non accetta piu' gli inviti).
      const ritorno = searchParams.get("returnUrl");
      if (ritorno && ritorno.startsWith("/invites/")) {
        navigate(ritorno, { replace: true });
        return;
      }

      // Si parte sempre dal piano gratuito (lo crea la registrazione): il
      // piano si sceglie alla fine della creazione del primo assistente o
      // dalla pagina Abbonamento. Nel wizard se c'e' un draft della landing
      // prompt-first, altrimenti in dashboard.
      navigate(
        sessionStorage.getItem("pingup-onboarding-mode")
          ? "/bots/new"
          : "/home",
        { replace: true }
      );
    } catch (err) {
      console.error("Registration error:", err);
      // Casi noti (email già registrata, validazione, piano base mancante)
      // con messaggio esplicito invece del codice grezzo dell'API.
      setError(describeApiError(err, t) || t("errors.api.registrationFailed"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center px-4 py-12 md:pt-[120px]">
      <div className="w-full max-w-[601px] flex flex-col gap-10">
        <div className="flex flex-col items-center gap-4 text-center">
          <img
            src="/pingup-logo-horizontal.svg"
            alt="PingUp"
            className="h-[52px] w-auto"
          />
          <h1 className="text-[32px] leading-9 font-semibold text-[#0a0a0a] mt-6">
            {t("auth.welcomeCreateBot")}
          </h1>
          <p className="text-base leading-7 text-muted-foreground">
            {t("auth.welcomeCreateBotSubtitle")}
          </p>
          <SignInPrompt t={t} />
        </div>

        <Card className="rounded-[14px] shadow-[0_5px_5px_rgba(0,0,0,0.1)]">
          <CardHeader>
            <CardTitle>{t("auth.signUp")}</CardTitle>
            <CardDescription>{t("auth.enterInfoToCreate")}</CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-5"
              >
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
                    {error}
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="firstName"
                    rules={{
                      required: t("auth.nameRequired"),
                      maxLength: {
                        value: 100,
                        message: t("auth.nameMaxLength"),
                      },
                    }}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("auth.firstName")}</FormLabel>
                        <FormControl>
                          <Input
                            type="text"
                            placeholder={t("auth.typeToEnter")}
                            autoComplete="given-name"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="lastName"
                    rules={{
                      required: t("auth.lastNameRequired"),
                      maxLength: {
                        value: 100,
                        message: t("auth.nameMaxLength"),
                      },
                    }}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("auth.lastName")}</FormLabel>
                        <FormControl>
                          <Input
                            type="text"
                            placeholder={t("auth.typeToEnter")}
                            autoComplete="family-name"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="email"
                  rules={{
                    required: t("auth.emailRequired"),
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: t("auth.emailInvalid"),
                    },
                  }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("auth.email")}</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder={t("auth.enterEmail")}
                          autoComplete="email"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="password"
                  rules={{
                    required: t("auth.passwordRequired"),
                    minLength: {
                      value: 8,
                      message: t("auth.passwordMinLength"),
                    },
                    pattern: {
                      value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                      message: t("auth.passwordPattern"),
                    },
                  }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("auth.password")}</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            type={showPassword ? "text" : "password"}
                            placeholder={t("auth.createPassword")}
                            autoComplete="new-password"
                            className="pr-10"
                            {...field}
                          />
                          <button
                            type="button"
                            tabIndex={-1}
                            onClick={() => setShowPassword((v) => !v)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                            aria-label={t("auth.password")}
                          >
                            {showPassword ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      </FormControl>
                      <FormMessage />
                      <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
                        <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                        {t("auth.passwordRequirements")}
                      </p>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="confirmPassword"
                  rules={{
                    required: t("auth.confirmPasswordRequired"),
                    validate: (value) =>
                      value === password || t("auth.passwordsMustMatch"),
                  }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("auth.confirmPassword")}</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            type={showConfirmPassword ? "text" : "password"}
                            placeholder={t("auth.confirmYourPassword")}
                            autoComplete="new-password"
                            className="pr-16"
                            {...field}
                          />
                          {passwordsMatch && (
                            <Check className="absolute right-9 top-1/2 -translate-y-1/2 h-4 w-4 text-[#00C875]" />
                          )}
                          <button
                            type="button"
                            tabIndex={-1}
                            onClick={() => setShowConfirmPassword((v) => !v)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                            aria-label={t("auth.confirmPassword")}
                          >
                            {showConfirmPassword ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="consent"
                  rules={{
                    validate: (value) =>
                      value === true || t("auth.consentRequired"),
                  }}
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-start gap-3 rounded-lg border p-4">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                            className="mt-0.5"
                          />
                        </FormControl>
                        <div className="space-y-1">
                          <FormLabel className="text-sm font-medium leading-snug cursor-pointer">
                            {t("auth.consentLabel")}
                          </FormLabel>
                          <p className="text-xs text-muted-foreground leading-relaxed">
                            {t("auth.consentDescription")}{" "}
                            <a
                              href={PRICING_TERMS_URL}
                              target="_blank"
                              rel="noreferrer"
                              className="underline hover:text-foreground"
                            >
                              {t("auth.consentPricingLink")}
                            </a>
                            .
                          </p>
                        </div>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button
                  type="submit"
                  className="w-full"
                  size="lg"
                  disabled={isLoading}
                >
                  {isLoading
                    ? t("auth.creatingAccount")
                    : t("auth.createAccount")}
                </Button>
              </form>
            </Form>

            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-card text-muted-foreground">
                    {t("auth.orContinueWith")}
                  </span>
                </div>
              </div>

              <div className="mt-6">
                <Button
                  type="button"
                  variant="secondary"
                  className="w-full"
                  onClick={() => {
                    window.location.href = `/api/sso/login?returnUrl=${encodeURIComponent(
                      window.location.origin
                    )}`;
                  }}
                >
                  {t("auth.signUpWithSSO")}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <SignInPrompt t={t} />
      </div>
    </div>
  );
};

export default Register;
